/* tslint:disable:component-selector */
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormArray, FormGroup, Validators, AbstractControl, FormBuilder } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { TslDialog } from '@tesla/ng-tds';
import { Observable, forkJoin, of } from 'rxjs';
import { QuillToolbarConfig } from 'ngx-quill';
import { getTemplateTranslationScope, htmlDecode, isNullOrUndefined } from 'app/shared/utils/utils';
import { DynamicFormService } from './dynamic-form.service';
import { IApiDetails, IField, IFormBodySection, ITaskFormJson } from 'app/profile/models/dynamic-form.model';
import { I18nService } from 'app/shared/services/i18n.service';
import { ApiService } from 'app/shared/services/api.service';

@Component({
    selector: 'dynamic-form',
    templateUrl: './dynamic-form.component.html',
    styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, OnChanges {
    @Output() actionEmitter = new EventEmitter();
    @Output() customEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() onFormCreated = new EventEmitter();
    translationResourcePath = 'onboarding/EhsQuestionnaire';
    language = 'en-US';
    @Input() formData;
    @Input() formSchema;
    @Input() isEsign = false;
    @Input() onDialogWindow = false;
    @Input() formDataValidation;
    primaryContactPerson = new FormControl();
    attachments: File[];
    loadingFormControls = true;
    dynamicFormArray: FormArray;
    actionsArray: any[];
    dynamicFormGroup = new FormGroup({});
    actionFormGroup: FormGroup;
    completeTaskBusy: false;
    suggestionDictionary: any = {};
    initialContactsOnLoad: any = {};
    showSpinner = false;
    rteModules;
    getTemplateTranslationScope = getTemplateTranslationScope;
    toolbarOptions: QuillToolbarConfig = [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['clean'],
        ['link'],
    ];
    _htmlDecode = htmlDecode;
    currentUser: any;
    constructor(
        private formControlService: DynamicFormService,
        private dialog: TslDialog,
        private translocoService: TranslocoService,
        private tasksService: DynamicFormService,
        public translate: I18nService,
        private formApiService: ApiService,
        private fb: FormBuilder
    ) { }

    get isDocumentLocalStorage(): boolean {
        return this.formData.taskTemplate.isLocalDocumentStorage;
    }

    get validationErrors(): { errorMessage: string; formField: string }[] {
        const errors = this.formDataValidation.errors;
        return Object.keys(errors)?.map((key) => ({ formField: key, errorMessage: errors[key] }));
    }

    ngOnInit(): void {
        this.setupComponent();
        this.rteModules = {
            toolbar: this.toolbarOptions,
        };
        this.language = localStorage.getItem('Profile-Culture') ? localStorage.getItem('Profile-Culture').replace(/"/g, "") : this.language;
        this.translocoService.setActiveLang(this.language);
        this.translocoService.langChanges$.subscribe((lang) => (this.language = lang));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['formSchema']) {
            this.setupComponent();
        }
    }

    setupComponent(): void {
            this._getDynamicData();
    }

    getSectionTableData(sectionIndex: number): any[] {
        return this.dynamicFormArray.value[sectionIndex];
    }

    onRemoveObject(sectionIndex: number, dataIndex: number): void {
        const sectionFormArray = this.dynamicFormArray.controls[sectionIndex] as FormArray;
        sectionFormArray?.controls?.splice(dataIndex, 1);
        sectionFormArray?.updateValueAndValidity();
        this.dynamicFormArray.updateValueAndValidity();
    }

    onEditObject(sectionIndex: number, dataIndex: number, section): void {
        const sectionFormArray = this.dynamicFormArray?.controls[sectionIndex] as FormArray;
        const formData = sectionFormArray?.controls[dataIndex]?.value;
    }

    private _getDynamicData(): void {
        const dynamicSections = this.formSchema?.formBody?.filter((sectionHeader) => {
            return sectionHeader.valueApi;
        });

        if (dynamicSections && dynamicSections.length > 0) {
            const sectionDataObservables: Observable<any>[] = dynamicSections.map((dynamicSection) => {
                if (dynamicSection.valueApi && dynamicSection.valueApi?.url) {
                    dynamicSection.valueApi = this.replaceApiDetailWithParamValues(dynamicSection.valueApi);
                    return null;
                }
            });

            this.showSpinner = true;
            forkJoin(sectionDataObservables).subscribe((data) => {
                data.forEach((sectionData, index) => {
                    // tslint:disable-next-line:no-eval
                    const result = eval(`(${dynamicSections[index]?.valueApi.valueMapper})`)(sectionData);
                    this.formData.taskResponseJson[dynamicSections[index].tableDataKey] = result;
                });
                this._bindData();
            });
        } else {
            this._bindData();
        }
    }

    private _bindData(): void {
        if (this.formSchema?.formBody) {
            this.dynamicFormArray = this.formControlService.toFormArray(
                this.formSchema.formBody,
                this.formData,
            );

            this.actionsArray = this.formControlService.toActionArray(this.formSchema.formAction);
            this.dynamicFormGroup = new FormGroup({
                dynamicFormArray: this.dynamicFormArray,
            });

            this.formSchema.formBody?.forEach((sectionHeader) => {
                sectionHeader.fields?.forEach((field: IField) => {
                    if (field.optionsApi) {
                        field.optionsApi = this.replaceApiDetailWithParamValues(field.optionsApi);
                    }
                    if (field.props?.isAutoCompleteApiInitiatedOnChange && field.optionsApi) {
                        this.showSpinner = true;

                        try {
                            this.formApiService
                                .get(field.optionsApi?.url)
                                .subscribe((data) => {
                                    try {
                                        // tslint:disable-next-line:no-eval
                                        const result = eval(`(${field.optionsApi?.optionsMapper})`)(data);
                                        this.initialContactsOnLoad[field.key] = result;
                                    } catch (error) {
                                        console.error(error);
                                    }
                                    this.showSpinner = false;
                                });
                        } catch (error) {
                            console.log(error);
                        }
                    }
                });
            });
        }

        this.loadingFormControls = false;
    }


    addFormArray() {
        this.dynamicFormArrays().push(this.newFormArray());
    }

    newFormArray(): FormGroup {
        return this.fb.group({
            formArray: this.formControlService.toFormArray(this.formSchema, this.formData),
        });
    }

    dynamicFormArrays(): FormArray {
        return this.dynamicFormGroup.get('dynamicFormArrays') as FormArray;
    }

    onEditorCreated(event, fieldControl: FormControl) {
        fieldControl.patchValue(htmlDecode(fieldControl.value));
    }

    onAction(action: any): void {
        let data;
        data = {
            action,
            taskResponseJson: this.formData?.taskResponseJson,
            formData: this.listToObject(this.dynamicFormArray.value),
            attachments: this.attachments ?? [],
            sectionFormGroup: this.dynamicFormArray.controls[0],
        };
        this.actionEmitter.emit(data);
    }

    handleFormChange() {
        let actionList = this.getPrimaryActions();
        let action = actionList.find(item=>item.type=="submit");
        let data;
        data = {
            action,
            taskResponseJson: this.formData?.taskResponseJson,
            formData: this.listToObject(this.dynamicFormArray.value),
            attachments: this.attachments ?? [],
            sectionFormGroup: this.dynamicFormArray.controls[0],
        };
        return {
            updatedData: data,
            updatedSchema: this.dynamicFormArray
        };
    }
    
    listToObject(formData = this.dynamicFormArrays().value): {} {
        if (formData) {
            if (formData.length > 0) {
                if (formData.length === 1 && formData[0]?.formArray) {
                    formData = formData[0]?.formArray;
                }
            }
            const tableDataKeys: string[] = this.formSchema?.formBody
                .filter((section) => section.isTableView)
                .map((section) => section.tableDataKey);
            let tableSectionCount = 0;
            const obj = formData.reduce((acc, cur) => {
                if (Array.isArray(cur)) {
                    const tableData = {};
                    tableData[tableDataKeys[tableSectionCount]] = cur;
                    cur = tableData;
                    tableSectionCount++;
                }

                return {
                    ...acc,
                    ...cur,
                };
            }, {});

            return { ...obj };
        }
        return {};
    }

    actionDisabled(action: any): boolean {
        let disabled = false;
        action?.enabledBy?.forEach((rule: { actionId: number; validation: string }) => {
            // find the action which current action is dependent on
            const dependency = this.actionsArray.find((el) => el.id === rule.actionId);
            // check if the dependency value matches required value
            // tslint:disable-next-line:no-eval
            if (!eval(rule.validation)(dependency.value)) {
                disabled = true;
            }
        });
        return disabled;
    }

    onAutocompleteKeyUp(event, formcontrol: FormControl, field) {
        const searchText = formcontrol.value;
        if (searchText && field.props?.isAutoCompleteApiInitiatedOnChange) {
            let result = this.initialContactsOnLoad[field.key];
            result = result.filter((item: any) => item.displayName.toLowerCase().includes(searchText.toLowerCase()));
            this.suggestionDictionary[field.key] = result;
        } else if (searchText && searchText.length > 2 && !field.props?.isAutoCompleteApiInitiatedOnChange) {
            this.showSpinner = true;
            let apiDetails = field.optionsApi;
            apiDetails = this.replaceApiDetailWithInputValue(apiDetails, searchText);
        } else {
            this.showSpinner = false;
            this.suggestionDictionary[field.key] = [];
        }
    }

    fieldHidden(field: IField, formGroup?: FormGroup): boolean {
        if (!field.displayedBy) {
            return false;
        }

        // find the field which current field is dependent on
        const formValue = this.listToObject(this.dynamicFormArray.value);
        // check if the dependency value matches required value
        // tslint:disable-next-line:no-eval
        const hidden = !eval(field.displayedBy)(formValue);

        if (field.props && (field?.props?.validationFn || field?.props?.isRequired)) {
            const formField = formGroup.get(field.key);
            this.resetFormValidators(formField, hidden, field);
        }
        return hidden;
    }

    sectionHidden(section: IFormBodySection, index?: number): boolean {
        if (!section.displayedBy) {
            return false;
        }

        // find the field which current field is dependent on
        const formValue = this.listToObject(this.dynamicFormArray.value);
        // check if the dependency value matches required value
        // tslint:disable-next-line:no-eval
        const hidden = !eval(section.displayedBy)(formValue);

        section.fields.forEach((field) => {
            if (field.props && (field?.props?.validationFn || field?.props?.isRequired)) {
                const formField = this.dynamicFormArray.at(index)?.get(field.key);
                this.resetFormValidators(formField, hidden, field);
            }
        });

        return hidden;
    }

    private resetFormValidators(formField: AbstractControl, hidden: boolean, field: IField) {
        if (hidden) {
            formField?.reset('');
            formField?.setValidators(null);
            formField?.updateValueAndValidity();
        } else {
            const validators = [];
            if (field.props.isRequired) {
                validators.push(Validators.required);
            }
            formField?.setValidators(validators);
            formField?.updateValueAndValidity();
        }
    }

    enabled(): boolean {
        return false;
    }

    getPrimaryActions(): any[] {
        if (this.onDialogWindow) {
            return [];
        } else {
            return this.actionsArray?.filter((action: any) => action.isPrimary);
        }
    }

    getSecondaryActions(): any[] {
        return this.actionsArray?.filter((action: any) => !action.isPrimary);
    }

    disableSubmit(): boolean {
        return !this.dynamicFormGroup.valid;
    }

    onFileChange(evt: File[], secondaryAction: any): void {
        this.attachments = evt ?? [];
        secondaryAction.value = evt;
    }

    onFileFormControlChange(evt: File[], formField: AbstractControl): void {
        formField?.patchValue(evt);
    }
    
    getFieldAttachments(field: any): any {
        if (this.toGetFileFromUrl(field)) {
            return this.formData[field.key];
        } else {
            return [];
        }
    }

    fieldFileAction(event) {
        this.actionEmitter.emit(event);
    }

    getAttachments(field: any): any {
        if (this.toGetFileFromUrl(field)) {
            return this.formData[field.key];
        } else if (this.isDocumentLocalStorage) {
            return this.formData.attachments;
        }
    }

    toGetFileFromUrl(field: any): boolean {
        return field.key in this.formData;
    }

    onSelection(event: any, fieldName: string, formControl: FormControl): void {
        formControl.setValue(event.option.value);
        this.suggestionDictionary[fieldName] = [];
    }

    onCheckBoxSelection(event: any, formControl: FormControl){
        if(formControl.value == true){
            formControl.setValue(event?.props?.options[0]?.value);
        }else{
            formControl.setValue(null);
        }
        formControl.updateValueAndValidity();
    }

    getSectionTableDisplayedCols(section: IFormBodySection): string[] {
        return section.fields.filter((x) => x.isDisplayed).map((x) => x.key);
    }

    onValidationError() {
        this.dynamicFormArray.controls.forEach((formGroup: FormGroup) => {
            Object.keys(this.formDataValidation.errors).forEach((key) => {
                if (formGroup.controls[key]) {
                    formGroup.controls[key].setErrors({ incorrect: true });
                }
            });
        });
    }

    replaceApiDetailWithParamValues(apiDetails: IApiDetails): any {
        const jsonString = JSON.stringify(apiDetails);
        const list = jsonString.split('##');

        const result = list
            .map((element) =>
                element && this.formData[element] ? this.formData[element] : element,
            )
            .join('');

        return JSON.parse(result);
    }
    

    replaceApiDetailWithInputValue(apiDetails: IApiDetails, value: string): any {
        const jsonString = JSON.stringify(apiDetails);
        const result = jsonString.replace(/\*\*input\*\*/g, value);
        return JSON.parse(result);
    }

    calculateFormat(section) {
        if (section?.fields?.flex) {
            return section?.fields?.flex;
        } else {
            return (100 / section?.fields?.length) * 1;
        }
    }
}